import { useRef, useState } from 'react';
import { Captions, Download, Pencil, Search, Trash2, Upload } from 'lucide-react';
import { api } from '../lib/api';
import { useMediaList } from '../lib/live';
import { toast, useIsHost, usePerms, useSession } from '../lib/session';
import type { Media, OnlineSubResult, Subtitle } from '../lib/types';
import { ModalTitle } from '../ui';

const LANGS = ['en', 'ja', 'hi', 'zh', 'ko', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'ar', 'th', 'vi', 'id', 'ta', 'te', 'bn', 'ml', 'mr', 'ur'];

// Built once for the module; browsers without Intl.DisplayNames fall back to the bare code.
const names = typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'language' }) : null;
const langName = (code: string) => (code ? (names?.of(code) ?? code.toUpperCase()) : 'Unknown');

const PROVIDER: Record<OnlineSubResult['provider'], string> = { opensubtitles: 'OpenSubtitles', yify: 'YIFY', gestdown: 'Addic7ed' };

const SOURCE: Record<Subtitle['source'], string> = {
  embedded: 'In the video file',
  file: 'File on the drive',
  upload: 'Added here',
  opensubtitles: 'OpenSubtitles',
  online: 'From the web',
};

/** Lists a title's subtitle tracks and lets members with upload rights add or remove them. */
export function SubtitlesDialog({ m }: { m: Media }) {
  const perms = usePerms();
  const isHost = useIsHost();
  const me = useSession((s) => s.user);
  const refresh = useMediaList((s) => s.refresh);
  const input = useRef<HTMLInputElement>(null);
  const [lang, setLang] = useState('');
  const [busy, setBusy] = useState(false);
  // The API answers with the updated title before the library list refreshes: show the newer one.
  const [local, setLocal] = useState<Media | null>(null);
  const cur = local && local.id === m.id && Date.parse(local.updatedAt) >= Date.parse(m.updatedAt) ? local : m;
  const subs = cur.subtitles ?? [];

  const run = async (fn: () => Promise<Media>, ok?: string) => {
    setBusy(true);
    try {
      setLocal(await fn());
      if (ok) toast(ok, 'accent');
      refresh();
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File) =>
    run(async () => {
      const res = await api.uploadSubtitle(m.id, file, lang);
      toast(`${res.subtitle.label}: ${res.subtitle.cues} lines${res.charset && res.charset !== 'UTF-8' ? ` (converted from ${res.charset})` : ''}`, 'accent');
      return res.media;
    });

  return (
    <>
      <ModalTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Captions size={20} /> Subtitles
      </ModalTitle>
      <p className="muted" style={{ fontSize: 12.5, margin: '0 0 14px' }}>
        {m.title}: everyone picks their own language with the <b>CC</b> button in the player.
      </p>

      {subs.length === 0 ? (
        <div className="empty" style={{ padding: '18px 0' }}>
          No subtitles yet.
        </div>
      ) : (
        <div className="subs-list">
          {subs.map((s) => {
            const canDelete = perms?.upload && (isHost || s.addedBy === me?.id || m.uploadedBy === me?.id);
            return (
              <div className="subs-row" key={s.id}>
                <span className="lang-badge">{(s.lang || '??').toUpperCase()}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="cell-main truncate">{s.label}</div>
                  <div className="cell-sub">
                    {SOURCE[s.source]}
                    {s.origin && s.source !== 'embedded' ? ` · ${s.origin}` : ''} · {s.cues.toLocaleString()} lines
                  </div>
                </div>
                {perms?.upload && (
                  <button
                    className="icon-btn"
                    aria-label="Rename"
                    disabled={busy}
                    onClick={() => {
                      const label = prompt('Name shown in the player', s.label);
                      if (label !== null) void run(() => api.updateSubtitle(m.id, s.id, { label }));
                    }}
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {canDelete && (
                  <button
                    className="icon-btn"
                    aria-label="Remove"
                    disabled={busy}
                    onClick={() => confirm(`Remove “${s.label}”?`) && void run(() => api.deleteSubtitle(m.id, s.id), 'Subtitles removed')}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <OnlineSearch m={m} onAdded={(updated) => setLocal(updated)} />

      {perms?.upload && (
        <div className="subs-add">
          <select value={lang} onChange={(e) => setLang(e.target.value)} aria-label="Language" className="input" style={{ width: 'auto', flex: 'none' }}>
            <option value="">Language: from file name</option>
            {LANGS.map((c) => (
              <option key={c} value={c}>
                {langName(c)}
              </option>
            ))}
          </select>
          <button className="btn btn-accent" disabled={busy} onClick={() => input.current?.click()}>
            <Upload size={14} /> {busy ? 'Adding…' : 'Add subtitle file'}
          </button>
          <input
            ref={input}
            type="file"
            accept=".srt,.vtt,.ass,.ssa,.smi,.sub"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void upload(f);
            }}
          />
        </div>
      )}
      <p className="muted" style={{ fontSize: 12, margin: '14px 0 0', lineHeight: 1.5 }}>
        SRT, ASS/SSA, WebVTT, SAMI and MicroDVD in any encoding (UTF-8, Shift-JIS, GB18030, Big5, EUC-KR, Windows-125x).
        Text subtitles inside the video are added automatically, and so are files like <span className="mono">Film Name.en.srt</span> placed
        next to the video in the drive folder. Blu-ray image subtitles (PGS) can't be converted to text.
      </p>
    </>
  );
}

/** Search subtitle sites (YIFY, Addic7ed, OpenSubtitles) for this title, then add a pick. */
function OnlineSearch({ m, onAdded }: { m: Media; onAdded: (m: Media) => void }) {
  const [lang, setLang] = useState(() => {
    try {
      return (JSON.parse(localStorage.getItem('vb.subs') || '{}') as { lang?: string }).lang || 'en';
    } catch {
      return 'en';
    }
  });
  const [results, setResults] = useState<OnlineSubResult[] | null>(null);
  const [busy, setBusy] = useState<string>('');
  const [err, setErr] = useState('');
  const search = async () => {
    setBusy('search');
    setErr('');
    try {
      setResults((await api.searchSubtitles(m.id, lang)).results ?? []);
    } catch (e) {
      setErr((e as Error).message);
      setResults(null);
    } finally {
      setBusy('');
    }
  };
  const add = async (r: OnlineSubResult) => {
    setBusy(r.provider + r.id);
    try {
      const res = await api.addOnlineSubtitle(m.id, r);
      onAdded(res.media);
      toast(`Added ${res.subtitle.label}: ${res.subtitle.cues} lines`, 'accent');
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy('');
    }
  };
  return (
    <div className="os-search">
      <div className="subs-add" style={{ marginTop: 0 }}>
        <select value={lang} onChange={(e) => setLang(e.target.value)} aria-label="Language" className="input" style={{ width: 'auto', flex: 'none' }}>
          {LANGS.map((c) => (
            <option key={c} value={c}>
              {langName(c)}
            </option>
          ))}
        </select>
        <button className="btn btn-black" disabled={!!busy} onClick={search}>
          <Search size={14} /> {busy === 'search' ? 'Searching…' : 'Search online'}
        </button>
      </div>
      {err && <p className="note" style={{ marginTop: 10 }}>{err}</p>}
      {results && results.length === 0 && <p className="note" style={{ marginTop: 10 }}>Nothing found in {langName(lang)}.</p>}
      {results && results.length > 0 && (
        <div className="subs-list" style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto' }}>
          {results.slice(0, 12).map((r) => (
            <div className="subs-row" key={r.provider + r.id}>
              <span className="lang-badge">{r.lang.toUpperCase()}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="cell-main truncate" title={r.release}>
                  {r.release || 'Subtitle'}
                </div>
                <div className="cell-sub">
                  {r.hashMatch && <b style={{ color: 'var(--ink)' }}>Exact match for this file · </b>}
                  {PROVIDER[r.provider]}
                  {r.downloads > 0 ? ` · ${r.downloads.toLocaleString()} downloads` : ''}
                  {r.rating ? ` · rated ${r.rating}` : ''}
                  {r.sdh ? ' · SDH' : ''}
                  {r.forced ? ' · forced only' : ''}
                  {r.machineMade ? ' · machine-translated' : ''}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => add(r)}>
                <Download size={13} /> {busy === r.provider + r.id ? 'Adding…' : 'Add'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
